/**
 * Admin Context (ViewModel)
 * Manages admin panel state including panel selection, current page, and data
 * This follows the MVVM pattern by separating business logic from presentation
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PanelType } from '../types/models';
import { useAuth } from './AuthContext';

interface AdminContextType {
  currentPanel: PanelType;
  currentPage: string;
  setCurrentPanel: (panel: PanelType) => void;
  setCurrentPage: (page: string) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// Map admin roles to panel types
const roleToPanelMap: Record<string, PanelType> = {
  master: 'master',
  risk: 'risk',
  finance: 'finance',
  support: 'support',
  compliance: 'compliance',
  content: 'content',
};

// Map admin roles to their default dashboard page
const roleToDashboardMap: Record<string, string> = {
  master: 'dashboard',
  risk: 'risk-dashboard',
  finance: 'finance-dashboard',
  support: 'support-dashboard',
  compliance: 'compliance-dashboard',
  content: 'content-dashboard',
};

// Map panel types to their dashboard pages
const panelToDashboardMap: Record<PanelType, string> = {
  master: 'dashboard',
  risk: 'risk-dashboard',
  finance: 'finance-dashboard',
  support: 'support-dashboard',
  compliance: 'compliance-dashboard',
  content: 'content-dashboard',
};

const STORAGE_KEYS = {
  CURRENT_PANEL: 'admin_current_panel',
  CURRENT_PAGE: 'admin_current_page',
  SIDEBAR_OPEN: 'admin_sidebar_open',
};

export const AdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { admin } = useAuth();
  
  // Load persisted state from localStorage or use defaults
  const getInitialPanel = (): PanelType => {
    const savedPanel = localStorage.getItem(STORAGE_KEYS.CURRENT_PANEL) as PanelType;
    return savedPanel || 'master';
  };

  const getInitialPage = (): string => {
    const savedPage = localStorage.getItem(STORAGE_KEYS.CURRENT_PAGE);
    // Don't default to 'dashboard' as it's master-only
    // Will be set correctly in useEffect based on role
    return savedPage || '';
  };

  const [currentPanel, setCurrentPanelState] = useState<PanelType>(getInitialPanel());
  const [currentPage, setCurrentPage] = useState<string>(getInitialPage());
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN);
    return saved ? saved === 'true' : true;
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Persist state to localStorage whenever it changes (but skip on initial mount to avoid re-renders)
  const isMountedRef = React.useRef(false);
  
  useEffect(() => {
    if (isMountedRef.current) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_PANEL, currentPanel);
    }
  }, [currentPanel]);

  useEffect(() => {
    if (isMountedRef.current) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_PAGE, currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (isMountedRef.current) {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, String(isSidebarOpen));
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Set panel and default page based on admin role when admin loads
  // Always show the role-specific dashboard on login (ignore saved state)
  useEffect(() => {
    if (admin?.role && !isInitialized) {
      const panelForRole = roleToPanelMap[admin.role];
      const dashboardForRole = roleToDashboardMap[admin.role];
      
      if (panelForRole && dashboardForRole) {
        setCurrentPanelState(panelForRole);
        // One-shot deep link (e.g. from /seed.html): honor admin_force_page exactly once, then
        // clear it, so a direct link can open a specific page. Normal logins (no force key) still
        // reset to the role dashboard so they always start there.
        const forced = localStorage.getItem('admin_force_page');
        if (forced) {
          localStorage.removeItem('admin_force_page');
          setCurrentPage(forced);
        } else {
          setCurrentPage(dashboardForRole);
        }
      }
      
      setIsInitialized(true);
    }
  }, [admin?.role, isInitialized]);

  // When panel changes (only for master admin), reset to that panel's dashboard
  const handlePanelChange = (panel: PanelType) => {
    // Only master admin can change panels
    if (admin?.role === 'master') {
      setCurrentPanelState(panel);
      const dashboardForPanel = panelToDashboardMap[panel];
      setCurrentPage(dashboardForPanel);
    } else if (admin?.role && roleToPanelMap[admin.role] === panel) {
      // Non-master admins can set their own panel (though they only have one)
      setCurrentPanelState(panel);
    }
  };

  // Expose setCurrentPage with persistence
  const handlePageChange = (page: string) => {
    setCurrentPage(page);
  };

  return (
    <AdminContext.Provider
      value={{
        currentPanel: currentPanel,
        currentPage,
        setCurrentPanel: handlePanelChange,
        setCurrentPage: handlePageChange,
        isSidebarOpen,
        setIsSidebarOpen,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

